import { useTranslation } from 'react-i18next';
import { useUser } from '@/app/store/auth';
import { useTimezoneDisplay } from '@/contexts/TimezoneDisplayContext';
import { formatDateInUserTimezone, getEffectiveTimezone } from '@/utils/timezoneAdvanced';
import { detectUserTimezone } from '@/utils/timezone';

/**
 * DateTime Format Options
 */
export interface DateTimeFormatOptions {
    /** Include date in output */
    includeDate?: boolean;
    /** Include time in output */
    includeTime?: boolean;
    /** Include timezone abbreviation */
    includeTimezone?: boolean;
    /** Include seconds */
    includeSeconds?: boolean;
    /** Use 12-hour format (AM/PM) */
    hour12?: boolean;
    /** Date format style */
    dateStyle?: 'short' | 'medium' | 'long' | 'full';
    /** Time format style */
    timeStyle?: 'short' | 'medium' | 'long' | 'full';
    /** Custom locale (overrides user's language) */
    locale?: string;
}

/**
 * Hook for timezone-aware date and time formatting
 * 
 * Automatically converts UTC dates from database to user's local timezone
 * and formats them according to the user's language preference.
 */
export function useDateTimeFormat() {
    const { i18n } = useTranslation();
    const user = useUser();
    const { displayMode } = useTimezoneDisplay();
    const detectedTimezone = detectUserTimezone();

    // Get user's current language for locale
    const currentLocale = i18n.language;

    // Map i18n language codes to Intl locale codes
    const localeMap: Record<string, string> = {
        'en': 'en-US',
        'es': 'es-ES',
        'fr': 'fr-FR',
        'de': 'de-DE',
    };

    const locale = localeMap[currentLocale] || 'en-US';

    /**
     * Create Intl.DateTimeFormat options from our custom options
     */
    const createFormatOptions = (options: DateTimeFormatOptions = {}): Intl.DateTimeFormatOptions => {
        const {
            includeDate = true,
            includeTime = false,
            includeTimezone = false,
            includeSeconds = false,
            hour12 = true,
            dateStyle,
            timeStyle,
        } = options;

        const formatOptions: Intl.DateTimeFormatOptions = {};

        // Date options
        if (includeDate) {
            if (dateStyle) {
                formatOptions.dateStyle = dateStyle;
            } else {
                formatOptions.year = 'numeric';
                formatOptions.month = 'short';
                formatOptions.day = 'numeric';
            }
        }

        // Time options
        if (includeTime) {
            if (timeStyle) {
                formatOptions.timeStyle = timeStyle;
            } else {
                formatOptions.hour = '2-digit';
                formatOptions.minute = '2-digit';
                if (includeSeconds) {
                    formatOptions.second = '2-digit';
                }
                formatOptions.hour12 = hour12;
            }
        }

        // Timezone
        if (includeTimezone) {
            formatOptions.timeZoneName = 'short';
        }

        return formatOptions;
    };

    /**
     * Core formatting function
     */
    const formatDateTimeCore = (utcDateString: string, options: DateTimeFormatOptions = {}) => {
        const effectiveTimezone = getEffectiveTimezone(user?.timezone, displayMode, detectedTimezone);
        const formatOptions = createFormatOptions(options);
        const targetLocale = options.locale || locale;

        // Use Intl.DateTimeFormat with the effective timezone
        const formatOptionsWithTz = {
            ...formatOptions,
            timeZone: effectiveTimezone
        };

        const dateObj = new Date(utcDateString);
        return new Intl.DateTimeFormat(targetLocale, formatOptionsWithTz).format(dateObj);
    };

    /**
     * Format date only (e.g., "Jan 15, 2024")
     */
    const formatDate = (utcDateString: string, options: Omit<DateTimeFormatOptions, 'includeTime'> = {}) => {
        return formatDateTimeCore(utcDateString, { ...options, includeDate: true, includeTime: false });
    };

    /**
     * Format time only (e.g., "9:30 AM")
     */
    const formatTime = (utcDateString: string, options: Omit<DateTimeFormatOptions, 'includeDate'> = {}) => {
        return formatDateTimeCore(utcDateString, { ...options, includeDate: false, includeTime: true });
    };

    /**
     * Format date and time together (e.g., "Jan 15, 2024, 9:30 AM")
     */
    const formatDateTime = (utcDateString: string, options: DateTimeFormatOptions = {}) => {
        return formatDateTimeCore(utcDateString, { ...options, includeDate: true, includeTime: true });
    };

    /**
     * Format with timezone (e.g., "Jan 15, 2024, 9:30 AM EST")
     */
    const formatWithTimezone = (utcDateString: string, options: DateTimeFormatOptions = {}) => {
        return formatDateTimeCore(utcDateString, { ...options, includeTimezone: true });
    };

    /**
     * Get relative time (e.g., "2 hours ago", "in 3 days")
     */
    const getRelativeTime = (utcDateString: string) => {
        const localDate = new Date(utcDateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - localDate.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return 'just now';
        } else if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
        } else if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
        } else if (diffInSeconds < 2592000) {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days} day${days !== 1 ? 's' : ''} ago`;
        } else {
            return formatDate(utcDateString);
        }
    };

    /**
     * Get user's timezone information
     */
    const getTimezoneInfo = () => {
        const now = new Date();
        const effectiveTimezone = getEffectiveTimezone(user?.timezone, displayMode, detectedTimezone);
        
        // Get offset for the effective timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: effectiveTimezone,
            timeZoneName: 'short'
        });
        
        // Calculate offset
        const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
        const tzDate = new Date(now.toLocaleString('en-US', { timeZone: effectiveTimezone }));
        const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60); // in minutes
        
        return {
            timezone: effectiveTimezone,
            offset: offset, // in minutes
            offsetHours: offset / 60, // in hours
        };
    };

    /**
     * Convert local date to UTC for sending to database
     */
    const localToUtc = (localDate: Date): string => {
        return localDate.toISOString();
    };

    return {
        formatDate,
        formatTime,
        formatDateTime,
        formatWithTimezone,
        getRelativeTime,
        getTimezoneInfo,
        localToUtc,
        currentLocale: locale,
    };
}